import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../shared/service/api.service';
import { ApiFunctionName } from '../../shared/enum/api-function-name.enum';
import { ApiData } from '../../shared/model/api-data.model';
import { ApiProduct } from '../../shared/model/api-product.model';
import { LanguageService } from '../../shared/service/language.service';
import { copyMatchingKeyValues, isLoggedIn, Loading } from '../../utils';
import { CartService } from '../../shared/service/cart.service';
import { ApiOrderItemFeature } from '../../shared/model/api.order-item-feature.model';
import * as _ from 'lodash';
import { Title } from '@angular/platform-browser';
import { replaceParams } from '../../shared/service/translations.service';
import iziToast from 'izitoast';
import { Bootbox } from '../../bootbox';
import { AppStorage, StorageKey } from '../../storage';

declare var $: any;

@Component({
  selector: 'product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
})
export class ProductDetailsComponent implements OnInit {
  apiData: ApiData;
  product: ApiProduct;
  productId: number;
  product_quantity: number = 1;
  product_feature_price: number = 0;
  product_feature_value: number = 0;

  isLoggedIn: boolean = false;
  onlineOrderDisable: boolean;
  private originalFeatureGroups: any[] = [];

  constructor(
    private apiService: ApiService,
    private cartService: CartService,
    public languageService: LanguageService,
    private titleService: Title
  ) {
    const pathname = location.pathname;
    this.productId = parseInt(pathname.substring(pathname.lastIndexOf('/') + 1).split('.')[0]);

    // @ts-ignore
    this.product = {};
  }

  ngOnInit() {
    this.isLoggedIn = isLoggedIn();

    this.apiService.apiData(ApiFunctionName.GetMenu).subscribe((apiData) => {
      this.apiData = apiData;
      this.onlineOrderDisable = apiData.Settings.WebSiteOrderMode !== 0;

      Loading.show()
        .then(() => this.apiService.getProduct(this.productId))
        .then((data: ApiProduct) => {
          if (data === null) throw new Error();

          this.product = new ApiProduct(data);
          this.titleService.setTitle(`${this.apiData.Title} | ${this.product.Name}`);

          if (this.product.Currency === null) {
            this.product.Currency = 'TRY';
          }

          if (this.product.ImageUrl === null) {
            this.product.ImageUrl = this.apiData.ImageUrl;
          }

          this.originalFeatureGroups = data.ProductFeatureGroups ? JSON.parse(JSON.stringify(data.ProductFeatureGroups)) : [];

          const appSettings = AppStorage.getItem(StorageKey.APP_SETTINGS) || {};
          const showOptionsVal = Number(appSettings.ShowOptions || appSettings.showOptions || 0);

          if (showOptionsVal === 0) {
            this.product.ProductFeatureGroups = [];
          } else if (showOptionsVal === 2 && this.product.ProductFeatureGroups) {
            this.product.ProductFeatureGroups = this.product.ProductFeatureGroups.map((group: any) => {
              if (group.ProductFeature) {
                group.ProductFeature = group.ProductFeature.filter((f: any) => {
                  return f.Dafault === true || f.DefaultOption === true || f.Default === true || f.IsDefault === true;
                });
              }
              return group;
            }).filter((group: any) => group.ProductFeature && group.ProductFeature.length > 0);
          }

          try {
            this.product.LingoName = JSON.parse(this.product.LingoName);
            this.product.LingoDescription = JSON.parse(this.product.LingoDescription);

            if (this.product.LingoName && this.product.LingoName.hasOwnProperty(this.languageService.currentLanguage)) {
              this.product.Name = this.product.LingoName[this.languageService.currentLanguage];
            }

            if (this.product.LingoDescription && this.product.LingoDescription.hasOwnProperty(this.languageService.currentLanguage)) {
              this.product.Description = this.product.LingoDescription[this.languageService.currentLanguage];
            }
          } catch (e) {
            console.error('Exception caught while parsing lingos:', e);
          }

          if (this.product.ProductFeatureGroups != null) {
            for (let key in this.product.ProductFeatureGroups) {
              if (!this.product.ProductFeatureGroups.hasOwnProperty(key)) continue;

              const productGroup = this.product.ProductFeatureGroups[key];
              productGroup.ProductFeatureChunk = _.chunk(productGroup.ProductFeature, 2);
            }
          }

          setTimeout(() => {
            this.initDefaultFeatures();
            this.calculateFeatures();
          }, 150);

        })
        .catch(() => {
          const translations = this.languageService.translations;
          iziToast.error({
            position: 'topRight',
            title: translations['general.error'],
            message: translations['general.unexpected_error_occured'],
          });
        })
        .finally(() => {
          Loading.hide();
        });
    });
  }

  getCurrencySymbol(currencyCode?: string): string {
    const code = currencyCode || (this.product && this.product.Currency) || 'TRY';

    const lang = (this.languageService && this.languageService.currentLanguage
      ? this.languageService.currentLanguage
      : 'TR').toUpperCase();

    if (replaceParams && (replaceParams as any)[code] && (replaceParams as any)[code][lang]) {
      return (replaceParams as any)[code][lang];
    }

    if (replaceParams && (replaceParams as any)[code] && (replaceParams as any)[code]['TR']) {
      return (replaceParams as any)[code]['TR'];
    }

    const serviceTranslations = this.languageService ? this.languageService.translations : null;
    if (serviceTranslations && serviceTranslations[code] && serviceTranslations[code][lang]) {
      return serviceTranslations[code][lang];
    }

    const symbolMap: { [key: string]: string } = {
      TRY: '₺',
      USD: '$',
      EUR: '€',
      GBP: '£',
      RUB: 'руб',
      AZN: '₼',
      KGS: 'c.',
      KZT: '₸'
    };

    return symbolMap[code] || code;
  }

  initDefaultFeatures() {
    const appSettings = AppStorage.getItem(StorageKey.APP_SETTINGS) || {};
    const showOptionsVal = Number(appSettings.ShowOptions || appSettings.showOptions || 0);

    const groupsToProcess = showOptionsVal === 0 ? this.originalFeatureGroups : this.product.ProductFeatureGroups;
    if (!groupsToProcess) return;

    groupsToProcess.forEach((group) => {
      const categoryId = group.Category !== undefined ? group.Category : group.ProductFeatureGroupID;
      if (categoryId === 2 || categoryId === 15) {
        if (group.ProductFeature && group.ProductFeature.length > 0) {
          const defaultFeature = group.ProductFeature.find(
            (f: any) => f.Dafault === true || f.DefaultOption === true || f.Default === true || f.IsDefault === true
          );

          if (defaultFeature) {
            const inputEl = $(`#item-${defaultFeature.ProductFeatureID}`);
            if (inputEl.length) {
              inputEl.prop('checked', true);
            }
          }
        }
      }
    });
  }

  toggleFeatureOption(feature: any, featureGroup: any, event: Event) {
    if (!this.onlineOrderDisable) return;

    const target = event.target as HTMLElement;

    if (target.tagName.toLowerCase() === 'input') {
      this.calculateFeatures();
      return;
    }

    const inputEl = $(`#item-${feature.ProductFeatureID}`);
    if (!inputEl.length) return;

    if (featureGroup.Type !== 1 || feature.Countable) {
      $(`.product-feature[name="fg-${feature.ProductFeatureGroupID}"]`).prop('checked', false);
      inputEl.prop('checked', true);
    } else {
      const isChecked = inputEl.prop('checked');
      inputEl.prop('checked', !isChecked);
    }

    this.calculateFeatures();
  }

  calculateFeatures() {
    this.product_feature_price = 0;
    this.product_feature_value = 0;

    const appSettings = AppStorage.getItem(StorageKey.APP_SETTINGS) || {};
    const showOptionsVal = Number(appSettings.ShowOptions || appSettings.showOptions || 0);

    if (showOptionsVal === 0) {
      if (this.originalFeatureGroups) {
        this.originalFeatureGroups.forEach((group) => {
          const categoryId = group.Category !== undefined ? group.Category : group.ProductFeatureGroupID;
          if (categoryId === 2 || categoryId === 15) {
            if (group.ProductFeature) {
              const defaultFeature = group.ProductFeature.find(
                (f: any) => f.Dafault === true || f.DefaultOption === true || f.Default === true || f.IsDefault === true
              );
              if (defaultFeature) {
                const price = parseFloat(defaultFeature.Price) || 0;
                const value = parseFloat(defaultFeature.Value) || 0;
                const isCountable = defaultFeature.Countable === true || defaultFeature.Countable === 1;

                if (!isCountable) {
                  this.product_feature_price += value > 0 ? value : price;
                } else {
                  this.product_feature_value += price > 0 ? price : value;
                }
              }
            }
          }
        });
      }
      return;
    }

    $('.product-feature:checked').each((index, element) => {
      const el = $(element);

      const price = parseFloat(el.attr('data-feature-price')) || 0;
      const value = parseFloat(el.attr('data-feature-value')) || 0;
      const isCountable = el.attr('data-feature-countable') === '1';

      if (!isCountable) {
        this.product_feature_price += value > 0 ? value : price;
      } else {
        this.product_feature_value += price > 0 ? price : value;
      }
    });
  }

  onFeatureChange() {
    this.calculateFeatures();
  }

  onClickAddToCartButton() {
    let errorMessage = '';
    const translations = this.languageService.translations;

    const fqErr = this._checkFeaturesQuantity();

    if (fqErr !== null) {
      if (fqErr.Type === 0) {
        errorMessage = replaceParams(translations['product_details.feature_quantity_short_error'], {
          amount: fqErr.ShortAmount,
          name: fqErr.FeatureGroup.Name,
        });
      }

      if (fqErr.Type === 1) {
        errorMessage = replaceParams(translations['product_details.feature_quantity_long_error'], {
          amount: fqErr.FeatureGroup.Quantity,
          name: fqErr.FeatureGroup.Name,
        });
      }

      Bootbox.alert(errorMessage);
      return;
    }

    const orderItem = this.product.createOrderItem();
    orderItem.Quantity = this.product_quantity;
    orderItem.OrderItemFeatures = this._getSelectedFeatures();

    const basePrice = this.product.Price || 0;
    orderItem.Price = basePrice;
    orderItem.UnitPrice = basePrice;

    const result = this.cartService.add(orderItem);

    if (result === false) {
      iziToast.error({
        position: 'topRight',
        title: translations['general.error'],
        message: translations['general.cart_cant_add_diff_menu'],
      });
      return;
    }

    iziToast.success({
      position: 'topRight',
      title: translations['general.success'],
      message: translations['general.item_added_to_cart'],
    });
  }

  _checkFeaturesQuantity() {
    const appSettings = AppStorage.getItem(StorageKey.APP_SETTINGS) || {};
    const showOptionsVal = Number(appSettings.ShowOptions || appSettings.showOptions || 0);

    if (showOptionsVal === 0) {
      return null;
    }

    const productObj: any = this.product;

    if (!productObj || !productObj.ProductFeatureGroups || productObj.ProductFeatureGroups.length === 0) {
      return null;
    }

    let hasError = null;

    for (let fg of productObj.ProductFeatureGroups) {
      const group: any = fg;
      const categoryId = group.Category !== undefined ? group.Category : group.ProductFeatureGroupID;

      if (categoryId !== 2 && categoryId !== 15 && showOptionsVal === 2) {
      }

      const features: any[] = group.ProductFeature;

      if (!features || features.length === 0) continue;

      for (let f of features) {
        const feature: any = f;
        const isChecked = $(`#item-${feature.ProductFeatureID}`).is(':checked');
        let selectedCount = isChecked ? 1 : 0;

        const minVal = parseFloat(feature.Min) || 0;
        const maxVal = parseFloat(feature.Max) || 0;

        if (minVal > 0 && selectedCount < minVal) {
          hasError = {
            FeatureGroup: group,
            Feature: feature,
            ShortAmount: minVal,
            Type: 0
          };
          break;
        }

        if (maxVal > 0 && selectedCount > maxVal) {
          hasError = {
            FeatureGroup: group,
            Feature: feature,
            ShortAmount: maxVal,
            Type: 1
          };
          break;
        }
      }

      if (hasError !== null) break;
    }

    return hasError;
  }

  _getSelectedFeatures() {
    let features: ApiOrderItemFeature[] = [];
    const appSettings = AppStorage.getItem(StorageKey.APP_SETTINGS) || {};
    const showOptionsVal = Number(appSettings.ShowOptions || appSettings.showOptions || 0);

    if (showOptionsVal === 0) {
      if (this.originalFeatureGroups) {
        this.originalFeatureGroups.forEach((group) => {
          const categoryId = group.Category !== undefined ? group.Category : group.ProductFeatureGroupID;
          if (categoryId === 2 || categoryId === 15) {
            if (group.ProductFeature) {
              const defaultFeature = group.ProductFeature.find(
                (f: any) => f.Dafault === true || f.DefaultOption === true || f.Default === true || f.IsDefault === true
              );
              if (defaultFeature) {
                let orderItemFeature = new ApiOrderItemFeature();
                copyMatchingKeyValues(defaultFeature, orderItemFeature);
                features.push(orderItemFeature);
              }
            }
          }
        });
      }
      return features;
    }

    $('.feature-group-container').each((index: number, element: HTMLElement) => {
      let featureGroupId = parseInt($(element).attr('data-feature-group-id'));

      const productFeatureGroup = this.product.ProductFeatureGroups
        .find((i) => i.ProductFeatureGroupID === featureGroupId);

      if (!productFeatureGroup) return;

      $(element).find('.product-feature:checked').each((index: number, element: HTMLElement) => {
        let featureId = parseInt($(element).attr('data-feature-id'));

        const feature = productFeatureGroup
          .ProductFeature
          .find((i) => i.ProductFeatureID === featureId);

        if (feature) {
          let orderItemFeature = new ApiOrderItemFeature();
          copyMatchingKeyValues(feature, orderItemFeature);
          features.push(orderItemFeature);
        }
      });
    });

    return features;
  }
}
